import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol } from "styles/global-style";
import Dropdown from "components/Dropdown";
import userIcon from "imgs/user.png";
import trashcan from "imgs/trashcan.png";

const ROLES = ["member", "lead", "manager", "admin", "owner"];

export default function Users() {
  const { supabase, user, refresh, roleAccess } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredTrashId, setHoveredTrashId] = useState(null);

  // Fetch all users in the organization with their membership types
  const fetchUsers = useCallback(async () => {
    if (!supabase || !user?.org?.id) return;

    setIsLoading(true);
    const { data, error } = await supabase
      .from("memberships")
      .select("*, people(*)")
      .eq("org_id", user.org.id)
      .neq("status", "deleted")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching users:", error);
      setIsLoading(false);
      return;
    }

    const processedUsers = (data || []).map((membership) => ({
      id: membership.people.id,
      membershipId: membership.id,
      firstname: membership.people.firstname || "",
      lastname: membership.people.lastname || "",
      email: membership.people.email || "",
      accessType: membership.type,
    }));

    setUsers(processedUsers);
    setIsLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Get available roles that the current user can assign
  // Owners can assign any role, others can only assign roles lower than their own
  const getAvailableRoles = useCallback(() => {
    if (!user?.role) return [];
    if (user.role === "owner") {
      return ROLES; // Owners can assign any role
    }
    const userRoleIndex = ROLES.indexOf(user.role);
    return ROLES.slice(0, userRoleIndex); // Only roles lower than user's own role
  }, [user]);

  // Check if a user's role can be changed
  // Owners can change any user (including other owners), others can only change users with lower roles
  const canChangeRole = useCallback(
    (targetUserRole) => {
      if (!user?.role) return false;
      if (user.role === "owner") {
        return true; // Owners can change anyone
      }
      const userRoleIndex = ROLES.indexOf(user.role);
      const targetRoleIndex = ROLES.indexOf(targetUserRole);
      return targetRoleIndex < userRoleIndex; // Only if target role is lower
    },
    [user]
  );

  // Handle access type change
  const handleAccessTypeChange = useCallback(
    async (userId, membershipId, newAccessType) => {
      if (!supabase || !user?.org?.id) return;

      // Check if this would remove the last owner
      const currentUser = users.find((u) => u.id === userId);
      if (currentUser?.accessType === "owner" && newAccessType !== "owner") {
        const otherOwners = users.filter((u) => u.id !== userId && u.accessType === "owner");
        if (otherOwners.length === 0) {
          alert("Cannot change access type: There must be at least one owner in the organization.");
          return;
        }
      }

      // Verify the new role is allowed (owners can assign any role, others can only assign roles lower than their own)
      if (user.role !== "owner") {
        const userRoleIndex = ROLES.indexOf(user.role);
        const newRoleIndex = ROLES.indexOf(newAccessType);
        if (newRoleIndex >= userRoleIndex) {
          alert("Cannot assign this access type: You can only assign roles lower than your own.");
          return;
        }
      }

      // Optimistically update local state for immediate UI feedback
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId ? { ...u, accessType: newAccessType } : u
        )
      );

      // Update the membership type
      const { error } = await supabase
        .from("memberships")
        .update({ type: newAccessType })
        .eq("id", membershipId)
        .eq("org_id", user.org.id);

      if (error) {
        console.error("Error updating access type:", error);
        alert("Failed to update access type. Please try again.");
        // Revert optimistic update on error
        await fetchUsers();
        return;
      }

      // Refresh users list and user context to ensure consistency
      await fetchUsers();
      await refresh();
    },
    [supabase, user, users, fetchUsers, refresh]
  );

  // Delete user (remove from organization by setting membership status to deleted)
  const handleDeleteUser = useCallback(
    async (userId, membershipId, displayName, accessType) => {
      if (!supabase || !user?.org?.id) return;

      const isCurrentUser = userId === user.id;
      if (isCurrentUser) {
        alert("You cannot remove yourself. Switch to another organization or use a different account.");
        return;
      }

      if (accessType === "owner") {
        const otherOwners = users.filter((u) => u.id !== userId && u.accessType === "owner");
        if (otherOwners.length === 0) {
          alert("Cannot remove the last owner from the organization.");
          return;
        }
      }

      if (!window.confirm(`Remove "${displayName}" from this organization? They will lose access to this org and its projects.`)) {
        return;
      }

      const { error } = await supabase
        .from("memberships")
        .update({ status: "deleted" })
        .eq("id", membershipId)
        .eq("org_id", user.org.id);

      if (error) {
        console.error("Error removing user:", error);
        alert("Failed to remove user. Please try again.");
        return;
      }

      await fetchUsers();
      await refresh();
    },
    [supabase, user, users, fetchUsers, refresh]
  );

  const isAdminOrHigher = roleAccess("admin");
  const availableRoles = getAvailableRoles();

  return (
    <FlexLeftCol>
      <h2>Users</h2>
      {isLoading ? (
        <p>Loading users...</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", paddingRight: 28 }}>
          {/* Header row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 0",
              fontSize: 12,
              fontWeight: 700,
              color: "#999",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: "1px solid #ddd",
            }}
          >
            <span style={{ textAlign: "left", flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <img src={userIcon} alt="" style={{ width: 9, height: 10, filter: "invert(1)" }} />
              Name
            </span>
            <span style={{ textAlign: "right", minWidth: 150 }}>Role</span>
          </div>
          {users.map((userItem) => {
            const displayName = `${userItem.firstname || ""} ${userItem.lastname || ""}`.trim() || userItem.email || "Unknown";
            const isCurrentUser = userItem.id === user.id;
            const canEdit = isAdminOrHigher && !isCurrentUser && canChangeRole(userItem.accessType);
            const canDelete =
              isAdminOrHigher &&
              !isCurrentUser &&
              (userItem.accessType !== "owner" || users.filter((u) => u.accessType === "owner").length > 1);

            return (
              <div
                key={userItem.id}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  fontSize: 14,
                  borderBottom: "1px solid #eeeeee22",
                }}
              >
                <span style={{ textAlign: "left", flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <img src={userIcon} alt="" style={{ width: 9, height: 10, filter: "invert(1)" }} />
                  {displayName}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    minWidth: 150,
                  }}
                >
                  {canEdit ? (
                    <Dropdown
                      key={`${userItem.id}-${userItem.accessType}`}
                      items={availableRoles}
                      defaultValue={userItem.accessType}
                      changeCb={(e) => handleAccessTypeChange(userItem.id, userItem.membershipId, e.target.value)}
                    />
                  ) : (
                    <span style={{ textTransform: "capitalize", fontWeight: 500 }}>{userItem.accessType}</span>
                  )}
                </div>
                {canDelete && (
                  <img
                    src={trashcan}
                    alt="Remove from organization"
                    title="Remove from organization"
                    style={{
                      position: "absolute",
                      right: -20,
                      width: 10,
                      height: 11,
                      cursor: "pointer",
                      filter:
                        hoveredTrashId === userItem.id
                          ? "brightness(0) saturate(100%) invert(63%) sepia(58%) saturate(3690%) hue-rotate(325deg) brightness(102%) contrast(101%)"
                          : "invert(1)",
                      opacity: 0.9,
                    }}
                    onMouseEnter={() => setHoveredTrashId(userItem.id)}
                    onMouseLeave={() => setHoveredTrashId(null)}
                    onClick={() =>
                      handleDeleteUser(userItem.id, userItem.membershipId, displayName, userItem.accessType)
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </FlexLeftCol>
  );
}
